import React, { useMemo, useEffect, useState } from 'react';
import { Redirect, useParams, Link } from 'react-router-dom';

import { OrderScreen } from '../OrderScreen';
import { OrderSuccessGrid, OrderSuccessGridData } from '../components/success/OrderSuccessGrid';
import { ReactComponent as ConfettiImg } from '../../../media/images/confetti.svg';
import { getRoktAttributes, AttributesType } from 'third-party-integrations/rokt/getRoktAttributes';

import { createStyles } from 'utils/createStyle';
import { Routes } from 'routes';
import { useRoktLauncher } from 'third-party-integrations/rokt';
import { useRootSelector } from 'common/features/featuresReducer';
import RemoteConfigWeb from 'config/RemoteConfigWeb';
import { requestRecommendedCategories } from 'common/features/store/duck/categories/duck';
import { useDispatch } from 'react-redux';
import useFeatureFlags, { FeatureFlagTypes } from 'common/hooks/useFeatureFlags';
import { ShopOnlineBanner } from 'features/wallet/components/ShopOnlineBanner';
import { ProductType } from 'common/api/e-comm/models/Product';
import {
    requestAffilateOffersForWalletCards,
    selectWalletAffiliateBonuses,
} from 'common/features/store/duck/wallet/duck';
import { useRefreshWalletPolling } from 'features/wallet/screens/MyCards';
import { constructorClient } from 'third-party-integrations/constructor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { cloDidNotLink } from 'common/api/e-comm/orders/service';
import { WalletCardAd } from 'features/wallet/components/WalletCardAd/WalletCardAd';
import { selectHasWalletAd } from 'common/features/store/duck/wallet-ads/duck';
import { useRequestWalletAdsIfNeeded } from 'common/hooks/useRequestWalletAdsIfNeeded';
import { Branding, IS_FLIPGIVE } from 'common/env';

type BrandDedupeList = { [key: number]: string[] };

const indexByBrandId = (
    config: {
        brandId: number;
        categoryNames: string[];
    }[]
): BrandDedupeList => {
    const dedupeList = {} as BrandDedupeList;
    for (const element of config) {
        const item = element;
        dedupeList[item.brandId] = item.categoryNames;
    }
    return dedupeList;
};

const styles = createStyles({
    confettiContainer: [
        'absolute hidden xl:block',
        {
            top: '-130px',
            left: '-190px',
            zIndex: -1,
        },
    ],
});

export type OrderSuccessScreenContentProps = {
    orderId: string;
};

export const OrderSuccessScreenContent = ({ orderId }: OrderSuccessScreenContentProps) => {
    const allCategories = useRootSelector(
        (state) => state.store.categories.recommendedCategory?.categories ?? []
    );
    const profile = useRootSelector((state) => state?.store?.home?.homeDetails?.profile);
    const orderData = useMemo(() => {
        const placedOrderJSON = sessionStorage.getItem(`placed_order_${orderId}`);
        if (placedOrderJSON) {
            return JSON.parse(placedOrderJSON) as OrderSuccessGridData;
        }
    }, [orderId]);

    const { isEnabled } = useFeatureFlags();
    const [attributes, setAttributes] = useState<AttributesType | null>(null);
    const affiliateBrandOffersSelector = useRootSelector(selectWalletAffiliateBonuses);
    const hasWalletAdSelector = useRootSelector(selectHasWalletAd);

    // Get brand info from order data for single eGift card orders
    const orderBrand = useMemo(() => {
        if (
            orderData?.cart.lineItems.length === 1 &&
            orderData.cart.lineItems[0].product.type === ProductType.EGiftCard
        ) {
            return orderData.cart.lineItems[0].brand;
        }
        return null;
    }, [orderData]);

    const brandId = orderBrand?.id ?? null;
    const isAdEligible = brandId ? hasWalletAdSelector(brandId) : false;

    const dispatch = useDispatch<any>();
    const requestWalletAdsIfNeeded = useRequestWalletAdsIfNeeded();

    useRefreshWalletPolling();

    // Fetch wallet ads from Contentful
    // Note: Ads are non-critical - errors are handled in the hook, but we're defensive here too
    useEffect(() => {
        try {
            requestWalletAdsIfNeeded();
        } catch (error) {
            // Wallet ads are non-critical - ensure wallet continues to function
            console.error('Failed to request wallet ads in OrderSuccessScreen:', error);
        }
    }, [requestWalletAdsIfNeeded]);

    const affiliateOffers = useMemo(() => {
        if (brandId && affiliateBrandOffersSelector) {
            return affiliateBrandOffersSelector(`${brandId}`);
        }
    }, [brandId, affiliateBrandOffersSelector]);

    /*
     * FIXES Bug where the ShopOnlineBanner _does not_ render due to the `brand`
     * that was just currently purchased  not appearing in the user's wallet.
     *
     * The user's wallet is used to fetch brand data, this is done inside
     * `useRefreshWalletPolling`
     *
     * Note: Do we _really_ need to use the hook here?
     */
    useEffect(() => {
        if (brandId) {
            // Fetches the brand data for the user's pucharsed gift card
            dispatch(requestAffilateOffersForWalletCards([brandId], constructorClient));
        }
    }, [brandId]);

    const launcher = useRoktLauncher();

    const dedupeList = useMemo(() => {
        return indexByBrandId(
            JSON.parse(RemoteConfigWeb.getRemoteValue('rokt_categories_dedupe_list').asString())
        );
    }, []);

    useEffect(() => {
        if (!isEnabled(FeatureFlagTypes.enable_rokt_web)) {
            return;
        }
        if (!profile || !allCategories.length || !dedupeList || !orderData) {
            return;
        }

        if (!attributes) {
            setAttributes(
                getRoktAttributes({
                    amount: orderData?.total ?? 0,
                    profile,
                    cartItems: orderData?.cart.lineItems ?? [],
                    allCategories,
                    dedupeList,
                })
            );
        }
    }, [allCategories, dedupeList, profile, orderData, isEnabled]);

    useEffect(() => {
        const fetchData = async () => {
            await requestRecommendedCategories()(dispatch);
        };
        fetchData();
    }, [dispatch]);

    useEffect(() => {
        if (orderData) {
            const { data, cart } = orderData;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.cnstrcPurchaseData = {
                items: [
                    ...cart.lineItems.map((item) => {
                        return {
                            item_id: item.brand?.id,
                            quantity: item.quantity,
                        };
                    }),
                ],

                order_id: data.orderId,
                revenue: cart.subtotal,
            };
        }
    }, [orderData]);

    useEffect(() => {
        if (!launcher || !attributes) {
            return;
        }

        const selectionPromise = launcher
            .selectPlacements({
                attributes: attributes,
                // instead of using the identifier, it was created an rule in the rokt dashboard
                // identifier: 'confirmation.page',
            })
            .catch(console.error);

        return () => {
            setAttributes(null);
            if (selectionPromise) {
                // When the page closes, remove all the Rokt placements
                selectionPromise.then((selection: { close: () => void }) => selection.close());
            }
        };
    }, [launcher, attributes]);

    return (
        <div>
            <div className="text-center">
                <div className="text-center text-3xl lg:text-5xl text-brand-dark mb-8">
                    <p>Your order has been received</p>
                    <p className="text-xl font-semibold">
                        An order confirmation has been sent to your email.
                    </p>
                </div>
                <div className="relative inline-block text-left">
                    <div className={styles.confettiContainer}>
                        <ConfettiImg />
                    </div>
                    {/* Wallet Ad for eligible brands */}
                    {isAdEligible && orderBrand && (
                        <div className="mb-4">
                            <WalletCardAd
                                cardBrand={{
                                    id: orderBrand.id,
                                    name: orderBrand.name,
                                    imageUrl: orderBrand.imageUrl,
                                }}
                                location="gift_card_details"
                            />
                        </div>
                    )}
                    {/* Shop Online Banner for double-dip brands (shown if no wallet ad) */}
                    {!isAdEligible && affiliateOffers && affiliateOffers.canDoubleDip && (
                        <div>
                            <ShopOnlineBanner
                                brandId={brandId!}
                                affiliateOffer={affiliateOffers}
                                atRaiseRight={false}
                            />
                            <div className="text-xs text-grey-2 mt-2">
                                When you buy through the link on {Branding}, we may earn a
                                commission.
                            </div>
                        </div>
                    )}
                    <div>
                        {orderData && cloDidNotLink(orderData.data.errorCode) ? (
                            <div>
                                <p className="border border-error text-error mt-8 mb-4 py-3 px-2 bg-error/[.10] font-semibold">
                                    <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        size="lg"
                                        className="mr-2 text-error"
                                    />
                                    Something went wrong linking your card for local dining. Please
                                    try again later.
                                </p>
                            </div>
                        ) : null}
                        <div className="text-right mb-1">
                            <a
                                href="#"
                                className="text-xs font-bold underline text-grey-1"
                                onClick={(e) => {
                                    print();
                                    e.preventDefault();
                                }}
                            >
                                Print Receipt
                            </a>
                        </div>
                        {orderData ? (
                            <OrderSuccessGrid data={orderData} />
                        ) : (
                            <Redirect to={Routes.ShopAccountSettingsOrderHistoryTab} />
                        )}
                    </div>
                    <div className="max-w-3xl text-sm pl-5 mt-5 text-grey-2">
                        <ul className="list-disc">
                            {!IS_FLIPGIVE && (
                                <li>
                                    Physical gift cards: Your coordinator can tell you when your
                                    gift cards will arrive. Not sure who your coordinator is? You
                                    can contact them via your{' '}
                                    <Link to={Routes.ShopAccountSettingsDashboardTab}>
                                        Dashboard
                                    </Link>
                                    .
                                </li>
                            )}
                            <li>
                                eGift cards: Your gift cards will be available in a few minutes. To
                                view and use your eGift cards, check your{' '}
                                <Link to={Routes.WalletMyCards}>Wallet</Link>.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div className="mb-5" />
            <div id="rokt-placeholder" className="max-w-3xl mr-auto ml-auto" />
        </div>
    );
};

export type OrderPaymentSuccessParams = {
    orderId: string;
};

export const OrderSuccessScreen = () => {
    const { orderId } = useParams<OrderPaymentSuccessParams>();

    return (
        <OrderScreen
            title="Order Success"
            orderStep="review"
            className="py-4 px-6 lg:px-8 sm:py-20 xl:mx-24 pt-20"
            showSummary={false}
            showMobileSummary={false}
            hideHeader={true}
            noMaxWidth={true}
            embedView={false}
        >
            <OrderSuccessScreenContent orderId={orderId} />
        </OrderScreen>
    );
};
